import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation } from 'react-query'
import { MediaTransformations, MediaVariant } from 'types'

type MediaVariantAttributes = {
  mediaFileId: string
  transformations: MediaTransformations
}

type Data = MediaVariant

type Variables = MediaVariantAttributes & {
  timeout?: number
  signal?: AbortSignal
}

const createMediaVariant = async (
  workspaceId: number,
  { mediaFileId, ...mediaVariantAttributes }: MediaVariantAttributes,
) => {
  const res = await sendToApi(`/w/${workspaceId}/media_files/${mediaFileId}/variants`, {
    method: 'POST',
    body: mediaVariantAttributes,
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data.media_variant
}

export const useCreateMediaVariant = () => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, Variables>((vars) => createMediaVariant(workspaceId, vars))
}

export const useCreateMediaVariantSync = () => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, Variables>(async (vars) => {
    const signal = vars.signal
    let mediaVariant: Data
    let elapsed = 0
    let interval = 500
    let timeout = vars.timeout || 10000 // 10 seconds

    do {
      mediaVariant = await createMediaVariant(workspaceId, vars)
      await new Promise((resolve) => {
        elapsed += interval
        setTimeout(resolve, interval)
      })
    } while (!mediaVariant?.source_url && elapsed < timeout && !signal?.aborted)

    if (signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }

    if (!mediaVariant?.source_url) {
      throw new Error('Failed to create media variant')
    }

    return mediaVariant
  })
}
